/**
 * "Needs you" dots for the sidebar (Oct 2026).
 *
 * One small orange dot on a page where something is waiting for a person,
 * with the reason in words for the hover label. A dot and not a count: the
 * sidebar is for finding your way, and a column of numbers turns it into a
 * second dashboard. No dot means nothing to do there.
 *
 * Read only. It reads the same cached lists the pages themselves load
 * (['drivers'], ['vehicles'], ['repairs'] under the org), so a page that is
 * already open costs nothing extra, and it never writes. A page the user
 * cannot open is not fetched at all: its query is handed no api, which
 * leaves it disabled.
 *
 * The rules mirror the pages they point at, so the dot and the page agree:
 *   Drivers      licence expired, or due within 60 days (the Drivers chips)
 *   Vehicles     tracker SIM expired, or due within 30 days (the SIM view)
 *   Maintenance  a job still open more than 30 days after it started
 */
import { useMemo } from 'react';
import { useDriversQuery } from './queries/useDriversQuery';
import { useVehiclesQuery } from './queries/useVehiclesQuery';
import { useRepairsQuery } from './queries/useRepairsQuery';

/* Same arithmetic as daysUntil in PageVisuals (midnight to midnight), copied
   rather than imported: PageVisuals is a large chunk the pages load lazily,
   and the shell should not pull it into the first download. */
const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const a = new Date(); a.setHours(0, 0, 0, 0);
  const b = new Date(d); b.setHours(0, 0, 0, 0);
  return Math.round((b - a) / 86400000);
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export function useNavSignals(api, orgId, authToken, canSee) {
  const { data: drivers } = useDriversQuery(canSee('drivers') ? api : null, orgId, authToken);
  const { data: vehicles } = useVehiclesQuery(canSee('vehicles') ? api : null, orgId, authToken);
  const { data: repairs } = useRepairsQuery(canSee('maintenance-repair') ? api : null, orgId, authToken);

  return useMemo(() => {
    const out = {};

    if (Array.isArray(drivers)) {
      let expired = 0; let due = 0;
      drivers.forEach((d) => {
        const days = daysUntil(d.driverLicenseExpiry);
        if (days == null) return;
        if (days < 0) expired += 1;
        else if (days <= 60) due += 1;
      });
      const words = [];
      if (expired) words.push(`${plural(expired, 'licence', 'licences')} expired`);
      if (due) words.push(`${due} due within 60 days`);
      if (words.length) out.drivers = words.join(', ');
    }

    if (Array.isArray(vehicles)) {
      let expired = 0; let soon = 0;
      vehicles.forEach((v) => {
        const days = daysUntil(v.trackerNumberRechargeDate);
        if (days == null) return;
        if (days < 0) expired += 1;
        else if (days <= 30) soon += 1;
      });
      const words = [];
      if (expired) words.push(`${plural(expired, 'tracker SIM', 'tracker SIMs')} expired`);
      if (soon) words.push(`${soon} due within 30 days`);
      if (words.length) out.vehicles = words.join(', ');
    }

    if (Array.isArray(repairs)) {
      const old = repairs.filter((r) => {
        if ((r.status || 'pending') === 'completed') return false;
        const days = daysUntil(r.startDate);
        return days != null && -days > 30;
      }).length;
      if (old) out['maintenance-repair'] = `${plural(old, 'job', 'jobs')} open over 30 days`;
    }

    return out;
  }, [drivers, vehicles, repairs]);
}
